import { groupLabelCode } from "./codes";
import type { LabelMedium, LabelPrintOptions, RollMedium, SheetMedium } from "./media";
import { QR_SIZE } from "./qr";

/*
 * Where every label goes on a roll or sheet (ADR 0018 › Printing): one pure
 * function the server's renderer and the browser's preview share, so the preview is what prints.
 * All lengths are millimetres, from the page's top left corner.
 */

export const MM_PER_INCH = 25.4;
export const MM_PER_PT = MM_PER_INCH / 72;

/** JetBrains Mono's advance width: every character is 600 of the em's 1000 units wide. */
const ADVANCE_EM = 0.6;
/** The code's size when none is chosen, by QR size. */
const DEFAULT_FONT_PT = { 3: 5, 4: 6.5 } as const;
/** Between the QR and the text below it, so the text never sits in the quiet zone. */
export const TEXT_GAP = 1;
/** Between the code's line and the part number's. */
const LINE_GAP = 0.5;
/** The cell's width with the part number line, about 12 mm; it fits most numbers at 4 pt. */
const PART_NUMBER_CELL_WIDTH = 12;
/** The smallest a part number shrinks to before it is cut off. */
const MIN_PART_NUMBER_PT = 3.5;
/**
 * Where a line's baseline sits, from its top, in ems: JetBrains Mono's capitals (0.73 em) end just
 * below the line's top, and descenders (0.2 em) above its bottom.
 */
export const BASELINE_EM = 0.8;
/** Blank space at the start and end of a roll's strip. */
export const END_MARGIN = 3;

/** How wide `text` is in JetBrains Mono at `fontSize` points. */
export function textWidth(text: string, fontSize: number): number {
  return text.length * ADVANCE_EM * fontSize * MM_PER_PT;
}

/** The largest size at which `text` fits `width`. */
function fittingFontSize(text: string, width: number): number {
  return text.length === 0
    ? Number.POSITIVE_INFINITY
    : width / (text.length * ADVANCE_EM * MM_PER_PT);
}

/** One label to print: its code and, with "Show part number", its part's primary number. */
export interface LabelToPrint {
  code: string;
  partNumber: string | null;
}

/** A cell's parts, relative to its top left corner. */
export interface CellGeometry {
  width: number;
  height: number;
  /** The QR's side, without the quiet zone, and one module's side. */
  qrSize: number;
  moduleSize: number;
  qrX: number;
  /** The code's line: its top, its height (the em) and its size in points. */
  codeTop: number | null;
  partNumberTop: number | null;
  lineHeight: number;
  fontSize: number;
}

/** One label on a page: where its cell is, and the text it shows. */
export interface PlacedLabel {
  /** Index into the labels given. */
  index: number;
  /** Its position on the strip or sheet, counted from 1 (the pick list's "position"). */
  position: number;
  x: number;
  y: number;
  codeText: string | null;
  partNumber: { text: string; fontSize: number } | null;
}

export interface LabelPage {
  width: number;
  height: number;
  labels: PlacedLabel[];
}

export type LayoutWarning =
  /** The code is wider than the cell: a wider cell or a smaller font fixes it. */
  | { kind: "codeTooWide"; minCellWidth: number; maxFontSize: number }
  /** These labels' part numbers don't fit even at the smallest size and are cut off. */
  | { kind: "partNumberCut"; codes: string[] }
  /** Not even one cell fits the printable width or the page. */
  | { kind: "cellTooLarge" }
  /** A label sheet's labels are smaller than the cell. */
  | { kind: "cellLargerThanLabel" };

export interface LabelLayout {
  cell: CellGeometry;
  /** Cells across, and down per page (a roll's whole strip). */
  columns: number;
  rows: number;
  pages: LabelPage[];
  warnings: LayoutWarning[];
}

/** The cell every label of a print gets, from the options (ADR 0018 › Layout defaults). */
export function cellGeometry(options: LabelPrintOptions, sampleCode = "0000000"): CellGeometry {
  const moduleSize = (options.dotsPerModule / options.dpi) * MM_PER_INCH;
  const qrSize = QR_SIZE * moduleSize;
  const fontSize = options.fontSize ?? DEFAULT_FONT_PT[options.dotsPerModule];
  const lineHeight = fontSize * MM_PER_PT;
  const needed = Math.max(
    qrSize,
    options.showCode ? textWidth(sampleCode, fontSize) : 0,
    options.showPartNumber ? PART_NUMBER_CELL_WIDTH : 0,
  );
  // The narrowest that fits, in half millimetres: 7.5 mm at 3 dots per module and 5 pt.
  const width = options.cellWidth ?? Math.ceil(needed * 2 - 1e-9) / 2;
  const hasText = options.showCode || options.showPartNumber;
  const codeTop = options.showCode ? qrSize + TEXT_GAP : null;
  const partNumberTop = options.showPartNumber
    ? qrSize + TEXT_GAP + (options.showCode ? lineHeight + LINE_GAP : 0)
    : null;
  const height =
    qrSize +
    (hasText ? TEXT_GAP : 0) +
    (options.showCode ? lineHeight : 0) +
    (options.showPartNumber ? (options.showCode ? LINE_GAP : 0) + lineHeight : 0);
  return {
    width,
    height,
    qrSize,
    moduleSize,
    qrX: (width - qrSize) / 2,
    codeTop,
    partNumberTop,
    lineHeight,
    fontSize,
  };
}

/** The code as a cell shows it: grouped (`K2W-2T8M`) where that fits, else as it is. */
export function codeText(code: string, cell: CellGeometry): string {
  const grouped = groupLabelCode(code);
  return textWidth(grouped, cell.fontSize) <= cell.width ? grouped : code;
}

/** A part number at the cell's size, smaller where it must be, and cut off below 3.5 pt. */
function fitPartNumber(
  text: string,
  cell: CellGeometry,
): { text: string; fontSize: number; cut: boolean } {
  const fontSize = Math.min(cell.fontSize, fittingFontSize(text, cell.width));
  if (fontSize >= MIN_PART_NUMBER_PT) return { text, fontSize, cut: false };
  const fits = Math.floor(cell.width / (ADVANCE_EM * MIN_PART_NUMBER_PT * MM_PER_PT));
  return {
    text: `${text.slice(0, Math.max(fits - 1, 0))}…`,
    fontSize: MIN_PART_NUMBER_PT,
    cut: true,
  };
}

/**
 * Lays labels out on a medium. A roll is one page, a strip as long as its rows; a sheet is as
 * many pages as the labels need, its first `skip` positions left empty (a part-used sheet).
 */
export function layoutLabels(input: {
  medium: LabelMedium;
  options: LabelPrintOptions;
  labels: readonly LabelToPrint[];
  skip?: number;
}): LabelLayout {
  const { medium, options, labels } = input;
  const cell = cellGeometry(options, labels[0]?.code);
  const warnings: LayoutWarning[] = [];
  if (options.showCode) {
    const longest = labels.reduce(
      (max, l) => (l.code.length > max.length ? l.code : max),
      "0000000",
    );
    if (textWidth(longest, cell.fontSize) > cell.width + 1e-9) {
      warnings.push({
        kind: "codeTooWide",
        minCellWidth: Math.ceil(textWidth(longest, cell.fontSize) * 10) / 10,
        maxFontSize: Math.floor(fittingFontSize(longest, cell.width) * 10) / 10,
      });
    }
  }
  const grid =
    medium.kind === "roll"
      ? rollGrid(medium, options, cell, labels.length)
      : sheetGrid(medium, options, cell, labels.length, input.skip ?? 0);
  warnings.push(...grid.warnings);

  const cut: string[] = [];
  const pages = grid.pages.map((page) => ({
    width: page.width,
    height: page.height,
    labels: page.slots.map(({ index, position, x, y }): PlacedLabel => {
      const label = labels[index] as LabelToPrint;
      let partNumber: PlacedLabel["partNumber"] = null;
      if (options.showPartNumber && label.partNumber) {
        const fitted = fitPartNumber(label.partNumber, cell);
        if (fitted.cut) cut.push(label.code);
        partNumber = { text: fitted.text, fontSize: fitted.fontSize };
      }
      return {
        index,
        position,
        x,
        y,
        codeText: options.showCode ? codeText(label.code, cell) : null,
        partNumber,
      };
    }),
  }));
  if (cut.length) warnings.push({ kind: "partNumberCut", codes: cut });
  return { cell, columns: grid.columns, rows: grid.rows, pages, warnings };
}

interface Slot {
  index: number;
  position: number;
  x: number;
  y: number;
}

interface Grid {
  columns: number;
  rows: number;
  pages: { width: number; height: number; slots: Slot[] }[];
  warnings: LayoutWarning[];
}

/** How many cells of `size` with `gap` between them fit into `room`. */
function fitting(room: number, size: number, gap: number): number {
  return Math.max(0, Math.floor((room + gap + 1e-9) / (size + gap)));
}

/**
 * A roll: columns across the printable width, centred on the roll, filled row by row, one strip:
 * rows × cell height + the gaps between them + an end margin at each end.
 */
function rollGrid(
  medium: RollMedium,
  options: LabelPrintOptions,
  cell: CellGeometry,
  count: number,
): Grid {
  const gap = options.gap;
  const columns = fitting(medium.printable, cell.width, gap);
  if (columns === 0)
    return { columns: 0, rows: 0, pages: [], warnings: [{ kind: "cellTooLarge" }] };
  const rows = Math.ceil(count / columns);
  const pageWidth = options.printableWidthOnly ? medium.printable : medium.width;
  const gridWidth = columns * cell.width + (columns - 1) * gap;
  const left = (pageWidth - gridWidth) / 2;
  const height = rows * cell.height + Math.max(rows - 1, 0) * gap + 2 * END_MARGIN;
  const slots = Array.from({ length: count }, (_, index) => ({
    index,
    position: index + 1,
    x: left + (index % columns) * (cell.width + gap),
    y: END_MARGIN + Math.floor(index / columns) * (cell.height + gap),
  }));
  return { columns, rows, pages: [{ width: pageWidth, height, slots }], warnings: [] };
}

/**
 * A sheet: a free grid within the margins, centred across, or the template's positions with one
 * cell centred in each. The first `skip` positions stay empty; the rest fill page after page.
 */
function sheetGrid(
  medium: SheetMedium,
  options: LabelPrintOptions,
  cell: CellGeometry,
  count: number,
  skip: number,
): Grid {
  const warnings: LayoutWarning[] = [];
  let columns: number;
  let rows: number;
  let place: (column: number, row: number) => { x: number; y: number };
  const template = medium.template;
  if (template) {
    columns = template.columns;
    rows = template.rows;
    if (cell.width > template.labelWidth + 1e-9 || cell.height > template.labelHeight + 1e-9) {
      warnings.push({ kind: "cellLargerThanLabel" });
    }
    place = (column, row) => ({
      x: template.offsetX + column * template.pitchX + (template.labelWidth - cell.width) / 2,
      y: template.offsetY + row * template.pitchY + (template.labelHeight - cell.height) / 2,
    });
  } else {
    const { top, right, bottom, left } = medium.margins;
    const roomX = medium.width - left - right;
    columns = fitting(roomX, cell.width, options.gap);
    rows = fitting(medium.height - top - bottom, cell.height, options.gap);
    const gridWidth = columns * cell.width + Math.max(columns - 1, 0) * options.gap;
    const startX = left + (roomX - gridWidth) / 2;
    place = (column, row) => ({
      x: startX + column * (cell.width + options.gap),
      y: top + row * (cell.height + options.gap),
    });
  }
  const perPage = columns * rows;
  if (perPage === 0)
    return { columns, rows, pages: [], warnings: [...warnings, { kind: "cellTooLarge" }] };
  const first = Math.min(Math.max(Math.floor(skip), 0), perPage - 1);
  const pages: Grid["pages"] = [];
  for (let index = 0; index < count; index++) {
    const slot = first + index;
    const pageIndex = Math.floor(slot / perPage);
    const onPage = slot % perPage;
    if (!pages[pageIndex])
      pages[pageIndex] = { width: medium.width, height: medium.height, slots: [] };
    const page = pages[pageIndex];
    page.slots.push({
      index,
      position: onPage + 1,
      ...place(onPage % columns, Math.floor(onPage / columns)),
    });
  }
  return { columns, rows, pages, warnings };
}
